import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";

export const TeleprompterGoogleMeetTeamsPT = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para Google Meet e Teams: Domine Suas Reuniões Profissionais
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Transmitir confiança e autoridade em reuniões virtuais é o novo diferencial executivo. <strong>Leia seus roteiros e notas discretamente</strong> enquanto mantém o contato visual perfeito.
        </p>

        <p className="text-slate-300 mb-8">
            Neste guia essencial para profissionais modernos, mostramos como transformar seu Google Meet ou Microsoft Teams em um verdadeiro palco corporativo com o PromptNinja. Aprenda a técnica do overlay invisível para manter o olhar fixo na lente, garantindo que você nunca perca o fio da meada ou esqueça um ponto crucial da sua apresentação. Descubra como nossa ferramenta online e discreta permite que você apresente com a segurança de um líder experiente, transmitindo presença executiva e domínio total do assunto, sem que sua audiência saiba que você está usando um teleprompter.
        </p>

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">Mantenha o Foco no Que Importa</h3>
            <p className="text-slate-300 mb-6">
                Fale olhando diretamente para a lente e transmita autoridade sem esforço.
                O <strong>PromptNinja</strong> é invisível para sua audiência e indispensável para você.
            </p>
            <a href="https://promptninja.solutionkit.com.br/#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Abrir Teleprompter Discreto
            </a>
        </div>

        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">A Batalha da Atenção: Notas vs Teleprompter</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> O "Leitor de Notas"
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Olhar:</strong> Desvia para baixo/lado a cada 10s</li>
                        <li>📉 <strong>Percepção:</strong> "Ele está lendo", "Não domina o assunto"</li>
                        <li>🤝 <strong>Conexão:</strong> Quebrada constantemente</li>
                        <li>😰 <strong>Ansiedade:</strong> Alta (medo de se perder no papel)</li>
                        <li>📺 <strong>Resultado:</strong> Reunião monótona, baixa conversão</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> O "Líder Conectado" (PromptNinja)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>👀 <strong>Olhar:</strong> 100% na lente da webcam</li>
                        <li>📈 <strong>Percepção:</strong> Autoritário, confiante, presente</li>
                        <li>🤝 <strong>Conexão:</strong> Intensa (olho no olho virtual)</li>
                        <li>😌 <strong>Ansiedade:</strong> Zero (o texto está ali, flutuando)</li>
                        <li>📺 <strong>Resultado:</strong> Engajamento total, alta persuasão</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Contato visual virtual = Poder. Quem domina a lente, domina a reunião.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Setups para Reuniões Online</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-indigo-500">
                <h3 className="text-xl font-bold text-indigo-400 mb-3">Setup #1: Monitor Único (Laptop) - "O Overlay Invisível"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Apenas seu notebook com webcam embutida. Ideal para viagens ou home office simples.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>Abra o Google Meet/Teams e maximize a janela.</li>
                    <li>Abra o PromptNinja em uma NOVA janela do navegador.</li>
                    <li>Redimensione o PromptNinja para ficar <strong>estreito e transparente</strong>.</li>
                    <li>Posicione essa janela EXATAMENTE no centro-topo da tela, logo abaixo da bolinha física da webcam.</li>
                    <li><strong>O Truque:</strong> Você lê o texto que está a 2cm da câmera. Para quem assiste, parece que você está olhando nos olhos deles.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Daily scrums, check-ins rápidos, nômades digitais.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-400 mb-3">Setup #2: Dual Monitor - "O Centro de Comando"</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Notebook + Monitor Externo.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Monitor 1 (com Webcam):</strong> É aqui que o PromptNinja fica. Centralizado no topo.</li>
                    <li><strong>Monitor 2 (Lateral):</strong> É aqui que ficam os rostos dos participantes do Teams/Meet ou sua apresentação PowerPoint.</li>
                    <li><strong>Fluxo:</strong> Você apresenta olhando para o Monitor 1 (Câmera + Texto). Consulta reações no Monitor 2 rapidamente.</li>
                    <li>Isso evita o erro clássico de olhar para o monitor lateral (perfil) enquanto fala com a câmera frontal.</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal para:</strong> Vendas complexas, aulas online, gestão de times.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #3: Entrevista de Emprego (Stealth Mode)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Cenário:</strong> Você está sendo entrevistado e quer colar suas respostas "STAR" sem parecer que decorou.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li>PromptNinja com opacidade 30% (quase invisível).</li>
                    <li>Texto em tópicos (bullet points), não frases completas (para soar natural).</li>
                    <li>Janela do Meets atrás do teleprompter, para você ver a reação do entrevistador ATRAVÉS do texto.</li>
                    <li>Use o recurso de rolagem automática MUITO lenta ou controle manual pelo teclado/mouse para não parecer robô.</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Pro tip:</strong> Não leia. Use como "cola" de palavras-chave para destravar sua memória.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-red-500">
            <h3 className="text-2xl font-bold text-white mb-4">🚨 O Perigo do Compartilhamento de Tela</h3>
            <p className="text-slate-300 mb-4">
                O erro #1 que destrói carreiras: compartilhar a tela errada e mostrar seu roteiro (ou seu WhatsApp) para o cliente.
            </p>
            <SEOContentHowTo
                title=""
                schemaTitle="Como Compartilhar Tela com Segurança no Teams/Meet"
                totalTime="PT1M"
                tools={["Google Meet/Teams", "PromptNinja"]}
                steps={[
                    {
                        title: "Regra de Ouro: Jamais 'Tela Inteira'",
                        text: "Nunca use a opção 'Share Entire Screen' (Tela Inteira). Isso mostra tudo, inclusive seu PromptNinja sobreposto."
                    },
                    {
                        title: "Use 'Share Window' (Janela)",
                        text: "No Meet/Teams, escolha 'Uma Janela' (A Window). Selecione APENAS o PowerPoint ou o PDF que quer mostrar."
                    },
                    {
                        title: "O Pulo do Gato",
                        text: "Como você compartilhou só a janela do PPT, o PromptNinja pode ficar fisicamente NA FRENTE do PPT na sua tela. VOCÊ vê o teleprompter por cima do slide. O CLIENTE vê apenas o slide limpo."
                    }
                ]}
            />
            <p className="text-slate-300 mt-4 text-sm">
                Isso permite que você tenha "Legendas da Realidade Aumentada" sobre sua própria apresentação, invisíveis para a audiência.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Erros que Entregam que Você Está Lendo</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #1: Olhos de Ping-Pong (Leitura Rápida)</h4>
                <p className="text-slate-300 text-sm">
                    Seus olhos se movem da esquerda para a direita rapidamente. Em telas grandes, isso é óbvio.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Reduza a LARGURA da janela do PromptNinja para 300px-400px. Quanto mais estreito o texto, menos seus olhos se movem lateralmente.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #2: Brilho do Monitor nos Óculos</h4>
                <p className="text-slate-300 text-sm">
                    O fundo branco do Word reflete nas suas lentes. Dá para ver o texto rolando no reflexo.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> PromptNinja tem fundo PRETO por padrão. Isso elimina 90% do reflexo. Ajuste o brilho do monitor para o mínimo confortável.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #3: Esquecer de Piscar e Sorrir</h4>
                <p className="text-slate-300 text-sm">
                    A "Cara de Leitura": olhos arregalados, pouca piscada, rosto sério. Assustador em HD.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Coloque emojis 😄 ou lembretes [SORRIA] e [RESPIRE] no meio do texto. Eles quebram o padrão robótico.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #4: Posicionar Janela Longe da Câmera</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter no rodapé da tela. Você parece olhar para o decote ou mesa de quem assiste.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> O texto deve estar no "Topo do Everest" da sua tela, o mais próximo possível da borda superior física.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Erro #5: Voz Monocórdica</h4>
                <p className="text-slate-300 text-sm">
                    Ler frases longas sem pausas naturais. Soa como síntese de voz.
                    <span className="text-green-400 block mt-2">✅ <strong>Solução:</strong> Escreva como você fala, não como escreve um e-mail. Use frases curtas. Use CAPS LOCK para palavras que precisam de ênfase vocal.</span>
                </p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">💼 Profissionalize Seu Home Office</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Não deixe que a tecnologia atrapalhe sua comunicação. Use-a para amplificar sua presença.
            </p>
            <a
                href="https://promptninja.solutionkit.com.br/#app"
                className="inline-block bg-gradient-to-r from-indigo-500 to-blue-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Preparar Reunião Agora (Grátis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Funciona no Chrome, Edge, Safari • Seguro e Privado</p>
        </div>

        <SEOContentFAQ
            title="Perguntas Frequentes - Reuniões Online"
            items={[
                {
                    question: "O Microsoft Teams ou Google Meet detectam que estou usando outra janela?",
                    answer: "Não o conteúdo. Eles sabem que você não está com a janela deles 'focada' (ativa), mas não sabem O QUE você está vendo. Para todos os efeitos, você pode estar olhando anotações. O PromptNinja é invisível para a API da chamada."
                },
                {
                    question: "Como faço para trocar slides e rolar o texto ao mesmo tempo?",
                    answer: "O ideal é não fazer os dois. Opção A: Texto automático (Play com velocidade baixa). Opção B: Use o Scroll do mouse para o texto e o Teclado (Setas) para os slides. Mantenha o foco do mouse na janela do PromptNinja."
                },
                {
                    question: "Funciona com fundo desfocado ou background virtual?",
                    answer: "Sim, isso é processado pela câmera/app de reunião. O PromptNinja é apenas uma janela na sua tela, não interfere no feed de vídeo da sua webcam. Ele ajuda você a OLHAR para a webcam."
                },
                {
                    question: "E se a internet cair?",
                    answer: "O PromptNinja é PWA e funciona Offline. Se sua internet cair no meio da reunião, o Meet/Teams vai travar, mas seu roteiro continuará lá, seguro, para quando você reconectar pelo 4G."
                },
                {
                    question: "Posso usar isso em entrevistas de emprego?",
                    answer: "Sim, é uma 'arma secreta' comum. A chave é não escrever o discurso inteiro. Escreva palavras-chave: 'PROJETO X - RESULTADO 20% - LIDERANÇA ÁGIL'. Olhe para essas palavras e fale naturalmente. Se ler frases inteiras, o recrutador (RH) treinado vai perceber."
                },
                {
                    question: "Preciso de um monitor ultrawide?",
                    answer: "Não, na verdade monitores muito grandes podem atrapalhar se você colocar o texto no canto. O segredo é sempre centralizar o texto logo abaixo da câmera, independente do tamanho do monitor."
                },
                {
                    question: "Funciona no Zoom e Webex também?",
                    answer: "Sim, funciona sobre QUALQUER aplicativo de videoconferência. O princípio é o mesmo: janela flutuante sobreposta, próximo à câmera, compartilhamento de janela (não tela)."
                }
            ]}
        />
    </>
);
